/**
 * Batches a bunch of inputs together. After a delay, calls the callback with an array of all the batched inputs.
 *
 * @class Batcher
 * @template T The input value type
 * @template S The output value type
 */
export class Batcher<T, S = any> {
    private queue: T[] = [];
    private promise: Promise<S[]> | undefined;
    private wake: (() => void) | undefined;

    constructor(
        private readonly batch: (req: T[]) => Promise<S[]>,
        private readonly delay: number = 50,
    ) {}

    async add(value: T): Promise<S> {
        if (!this.queue.length) {
            this.promise = (async () => {
                await this.waitForDelay();
                const list = this.queue;
                this.queue = [];
                return this.batch(list);
            })();
        }
        const index = this.queue.length;
        this.queue.push(value);

        const results = await this.promise!;
        const result = results[index];
        return result;
    }

    async wait() {
        return this.promise;
    }

    /**
     * Sends whatever is queued right now instead of waiting out the delay.
     * Resolves once the batch callback has finished, or immediately if nothing is queued.
     */
    async flush() {
        this.wake?.();
        return this.promise;
    }

    /** Sleeps for the batch delay, unless {@link flush} wakes us up first. */
    private waitForDelay(): Promise<void> {
        return new Promise<void>(resolve => {
            const done = () => {
                clearTimeout(timeout);
                this.wake = undefined;
                resolve();
            };
            const timeout = setTimeout(done, this.delay);
            this.wake = done;
        });
    }
}
